<template>
  <b-modal v-model="visible" size="md" centered hide-header hide-footer
           no-close-on-backdrop body-class="p-0" content-class="tbd-content">
    <!-- Hero -->
    <div class="tbd__hero">
      <div class="tbd__hero-icon"><i class="fa-solid fa-trash-can"></i></div>
      <div class="tbd__hero-text">
        <h4 class="mb-0">{{ $t('trade.bulkDelete.title', { count: targets.length }) }}</h4>
        <small>{{ orderSummary }}</small>
      </div>
      <button type="button" class="tbd__close" @click="close" :disabled="running">
        <feather-icon icon="XIcon" size="18"/>
      </button>
    </div>

    <div class="tbd__body">
      <b-alert show variant="danger" class="p-75 mb-0">
        <i class="fa-solid fa-triangle-exclamation mr-25"></i>{{ $t('trade.bulkDelete.warning') }}
      </b-alert>

      <!-- Progress -->
      <div v-if="running || progress.total > 0" class="tbd__progress mt-1">
        <b-progress :value="progress.done" :max="progress.total" height="8px"
                    :variant="progress.fail ? 'danger' : 'success'"/>
        <small class="text-muted">
          <template v-if="running">{{ $t('finance.actionModal.running', { done: progress.done, total: progress.total }) }}</template>
          <template v-else>
            {{ $t('finance.actionModal.resultOk', { count: progress.ok }) }}<template v-if="progress.fail"> · <span class="text-danger">{{ $t('finance.actionModal.resultFail', { count: progress.fail }) }}</span></template>
          </template>
        </small>
      </div>

      <!-- Footer -->
      <div class="tbd__footer">
        <b-button variant="outline-secondary" :disabled="running" @click="close">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="running || !targets.length" @click="run">
          <b-spinner v-if="running" small class="mr-25"/>
          <i v-else class="fa-solid fa-trash-can mr-25"></i>
          {{ $t('operations.delete') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'

/**
 * Bulk delete for open CFD orders (client modal → account trades): same
 * visual language as the monetary bulk-actions modal, delete only. Runs the
 * SAME per-trade delete the single row button uses (trade/deleteOrder),
 * sequentially with a progress bar.
 */
export default {
  name: 'TradeBulkDeleteModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    targets: [],
    account: null,
    running: false,
    progress: { done: 0, total: 0, ok: 0, fail: 0 },
  }),
  computed: {
    orderSummary() {
      const ids = this.targets.map(t => '#' + t.order)
      return ids.length > 6
          ? `${ids.slice(0, 6).join(' · ')} · +${ids.length - 6}`
          : ids.join(' · ')
    },
  },
  methods: {
    ...mapActions('trade', ['deleteOrder']),
    /**
     * @param {Array<Object>} trades selected trade rows
     * @param {*} [account] fallback account login — rows carrying their own
     *                      `account` (the main open-orders page spans several
     *                      accounts) win over it
     */
    open(trades, account) {
      this.targets = (trades || []).filter(Boolean)
      this.account = account != null ? account : null
      this.running = false
      this.progress = { done: 0, total: 0, ok: 0, fail: 0 }
      this.visible = true
    },
    close() {
      if (this.running) return
      this.visible = false
    },
    async run() {
      if (!this.targets.length || this.running) return
      this.running = true
      this.progress = { done: 0, total: this.targets.length, ok: 0, fail: 0 }
      for (const trade of this.targets) {
        try {
          await this.deleteOrder({
            account: trade.account != null ? trade.account : this.account,
            orderId: trade.order,
          })
          this.progress.ok++
        } catch (err) {
          this.progress.fail++
        }
        this.progress.done++
      }
      this.running = false
      if (this.progress.fail === 0) {
        this.showSuccessNotification(this.$t('finance.actionModal.resultOk', { count: this.progress.ok }))
        this.visible = false
      } else {
        this.showWarningNotification(
            `${this.$t('finance.actionModal.resultOk', { count: this.progress.ok })} · ${this.$t('finance.actionModal.resultFail', { count: this.progress.fail })}`)
      }
      this.$emit('done')
    },
  },
}
</script>

<style lang="scss">
/* Same visual language as the monetary/finance action modals */
.tbd-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.tbd__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #7f1d1d, #c1272d);
}
.tbd__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.tbd__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; word-break: break-word; } }
.tbd__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}

.tbd__body { padding: 1.15rem 1.4rem 1.3rem; }
.tbd__progress { display: flex; flex-direction: column; gap: .25rem; }
.tbd__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.1rem;
}

.dark-layout {
  .tbd-content { background: #283046; color: #d0d2d6; }
}
</style>
